import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import {
    PlusIcon,
    XMarkIcon,
    Bars3Icon,
    TrashIcon,
    LanguageIcon,
    ArrowLeftIcon,
    GlobeAltIcon,
    CheckIcon
} from '@heroicons/react/24/outline';
import { surveyApiService } from '../../api/surveyApi';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';

const SUPPORTED_LANGUAGES = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];

const SurveyCreate = () => {
    const { slug } = useParams();
    const isEdit = !!slug;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [questionTypes, setQuestionTypes] = useState([]);
    const [activeLanguages, setActiveLanguages] = useState(['tr', 'en']);

    // Modal states
    const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState({
        id: null,
        title: {},
        type_id: '',
        is_required: true,
        options: []
    });

    const [surveyData, setSurveyData] = useState({
        title: {},
        description: {},
        slug: '',
        is_active: true,
        is_anonymous: false,
        estimated_minutes: 5,
        primary_color: '#4F46E5',
        sections: [
            { id: Date.now(), title: { tr: 'Genel Bilgiler', en: 'General Information' }, questions: [] }
        ]
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch question types
                const typesResponse = await surveyApiService.getQuestionTypes();
                const types = Array.isArray(typesResponse) ? typesResponse : (typesResponse?.results || []);
                setQuestionTypes(types);

                // If in edit mode, fetch survey data
                if (isEdit) {
                    const survey = await surveyApiService.getSurveyDetail(slug);
                    setSurveyData(survey);
                    if (survey.languages && survey.languages.length > 0) {
                        setActiveLanguages(survey.languages);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, [slug, isEdit]);

    const toggleLanguage = (code) => {
        if (activeLanguages.includes(code)) {
            if (activeLanguages.length > 1) {
                setActiveLanguages(activeLanguages.filter(l => l !== code));
            } else {
                alert('En az bir dil seçili olmalıdır.');
            }
        } else {
            setActiveLanguages([...activeLanguages, code]);
        }
    };

    const handleSave = async () => {
        if (!surveyData.title[activeLanguages[0]] || !surveyData.slug) {
            alert('Please enter a title and slug');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...surveyData,
                languages: activeLanguages
            };
            if (isEdit) {
                await surveyApiService.updateSurvey(slug, payload);
            } else {
                await surveyApiService.createSurvey(payload);
            }
            navigate('/survey/list');
        } catch (error) {
            console.error('Error saving survey:', error);
            alert('Error saving survey: ' + (error.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const handleAddSection = () => {
        const newSection = {
            id: Date.now(),
            title: activeLanguages.reduce((acc, lang) => ({ ...acc, [lang]: `Yeni Bölüm ${surveyData.sections.length + 1}` }), {}),
            questions: []
        };
        setSurveyData({
            ...surveyData,
            sections: [...surveyData.sections, newSection]
        });
    };

    const handleDeleteSection = (id) => {
        if (surveyData.sections.length === 1) {
            alert('En az bir bölüm olmalıdır.');
            return;
        }
        setSurveyData({
            ...surveyData,
            sections: surveyData.sections.filter(s => s.id !== id)
        });
    };

    const handleAddQuestion = (sectionId) => {
        setActiveSectionId(sectionId);
        setCurrentQuestion({
            id: Date.now(),
            title: activeLanguages.reduce((acc, lang) => ({ ...acc, [lang]: '' }), {}),
            type_id: questionTypes[0]?.id || '',
            is_required: true,
            options: []
        });
        setIsQuestionModalOpen(true);
    };

    const handleSaveQuestion = () => {
        const primaryLang = activeLanguages[0];
        if (!currentQuestion.title[primaryLang]) {
            alert(`${SUPPORTED_LANGUAGES.find(l => l.code === primaryLang).name} dilde soru başlığı zorunludur.`);
            return;
        }

        const updatedSections = surveyData.sections.map(section => {
            if (section.id === activeSectionId) {
                return {
                    ...section,
                    questions: [...section.questions, currentQuestion]
                };
            }
            return section;
        });

        setSurveyData({ ...surveyData, sections: updatedSections });
        setIsQuestionModalOpen(false);
    };

    const handleAddOption = () => {
        setCurrentQuestion({
            ...currentQuestion,
            options: [...currentQuestion.options, {
                id: Date.now(),
                text: activeLanguages.reduce((acc, lang) => ({ ...acc, [lang]: '' }), {}),
                value: ''
            }]
        });
    };

    const handleRemoveOption = (optionId) => {
        setCurrentQuestion({
            ...currentQuestion,
            options: currentQuestion.options.filter(o => o.id !== optionId)
        });
    };

    const handleDeleteQuestion = (sectionId, questionId) => {
        const updatedSections = surveyData.sections.map(section => {
            if (section.id === sectionId) {
                return {
                    ...section,
                    questions: section.questions.filter(q => q.id !== questionId)
                };
            }
            return section;
        });
        setSurveyData({ ...surveyData, sections: updatedSections });
    };

    // Modal açıkken scroll kilitle
    useEffect(() => {
        if (isQuestionModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isQuestionModalOpen]);

    const selectedType = Array.isArray(questionTypes) ? questionTypes.find(t => t.id === parseInt(currentQuestion.type_id)) : null;
    const hasOptions = selectedType?.has_options;

    return (
        <>
            <div className="p-6 max-w-5xl mx-auto space-y-6">
                <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-6">
                    <div className="flex items-center space-x-4">
                        <button onClick={() => navigate('/survey/list')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                            <ArrowLeftIcon className="w-6 h-6" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {isEdit ? 'Edit Survey' : 'Create Multilingual Survey'}
                            </h1>
                            <p className="text-sm text-gray-500">
                                {isEdit ? 'Update your survey design' : 'Add questions in multiple languages'}
                            </p>
                        </div>
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={() => navigate('/survey/list')}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors border border-gray-200 dark:border-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={loading}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-semibold disabled:opacity-50"
                        >
                            {loading ? 'Saving...' : 'Save Survey'}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        <Card className="p-6 border-none shadow-sm dark:bg-gray-800">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-lg font-bold flex items-center">
                                    <LanguageIcon className="w-5 h-5 mr-2 text-blue-500" />
                                    General Information
                                </h2>
                            </div>
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {activeLanguages.map(lang => (
                                        <div key={lang}>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">
                                                Title ({lang.toUpperCase()})
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                                placeholder={`Survey Title in ${lang.toUpperCase()}`}
                                                value={surveyData.title[lang] || ''}
                                                onChange={(e) => setSurveyData({ ...surveyData, title: { ...surveyData.title, [lang]: e.target.value } })}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Slug (URL)</label>
                                    <input
                                        type="text"
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 outline-none transition-all font-mono text-sm"
                                        placeholder="customer-satisfaction-2024"
                                        value={surveyData.slug}
                                        onChange={(e) => setSurveyData({ ...surveyData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {activeLanguages.map(lang => (
                                        <div key={lang}>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">
                                                Description ({lang.toUpperCase()})
                                            </label>
                                            <textarea
                                                rows="3"
                                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                                value={surveyData.description?.[lang] || ''}
                                                onChange={(e) => setSurveyData({ ...surveyData, description: { ...surveyData.description, [lang]: e.target.value } })}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 ml-1">Anket Ana Rengi (Tema)</label>
                                    <div className="flex flex-wrap gap-3">
                                        {['#4F46E5', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#8B5CF6'].map(color => (
                                            <button
                                                key={color}
                                                onClick={() => setSurveyData({ ...surveyData, primary_color: color })}
                                                className={`w-10 h-10 rounded-full border-4 transition-all ${surveyData.primary_color === color ? 'border-gray-300 dark:border-gray-500 scale-110 shadow-lg' : 'border-transparent hover:scale-105'
                                                    }`}
                                                style={{ backgroundColor: color }}
                                            />
                                        ))}
                                        <div className="relative">
                                            <input
                                                type="color"
                                                value={surveyData.primary_color}
                                                onChange={(e) => setSurveyData({ ...surveyData, primary_color: e.target.value })}
                                                className="w-10 h-10 rounded-full border-none cursor-pointer bg-transparent"
                                            />
                                            <div className="absolute inset-0 rounded-full border-4 border-gray-100 dark:border-gray-700 pointer-events-none opacity-20" />
                                        </div>
                                        <span className="ml-2 flex items-center text-sm font-mono text-gray-500 uppercase tracking-tighter">{surveyData.primary_color}</span>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <h3 className="font-bold text-gray-900 dark:text-white">Survey Sections & Questions</h3>
                                <button
                                    onClick={handleAddSection}
                                    className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center transition-colors"
                                >
                                    <PlusIcon className="w-4 h-4 mr-1" /> Add Section
                                </button>
                            </div>

                            {surveyData.sections.map((section, sIndex) => (
                                <Card key={section.id} className="border-none shadow-sm dark:bg-gray-800 overflow-hidden">
                                    <div className="bg-gray-50 dark:bg-gray-900/50 p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                                        <div className="flex items-center space-x-3">
                                            <Bars3Icon className="w-5 h-5 text-gray-400 cursor-move" />
                                            <span className="font-bold">S{sIndex + 1}: {section.title[activeLanguages[0]]}</span>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteSection(section.id)}
                                            className="p-1 hover:text-red-600 transition-colors"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                    <div className="p-4 space-y-4">
                                        {section.questions.length === 0 ? (
                                            <div className="p-12 text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl m-4">
                                                <p className="text-gray-400 mb-4">No questions in this section yet</p>
                                                <button
                                                    onClick={() => handleAddQuestion(section.id)}
                                                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-sm font-bold"
                                                >
                                                    + Add Question
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {section.questions.map((q, qIndex) => (
                                                    <div key={q.id} className="p-4 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-xl flex justify-between items-center group">
                                                        <div className="flex items-center space-x-4">
                                                            <span className="text-xs font-bold text-gray-400">Q{qIndex + 1}</span>
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-sm font-semibold">{q.title[activeLanguages[0]]}</p>
                                                                    <span className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1.5 rounded text-gray-500">{activeLanguages.length} Lags</span>
                                                                </div>
                                                                <p className="text-xs text-gray-500">{questionTypes.find(t => t.id === parseInt(q.type_id))?.type_name}</p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => handleDeleteQuestion(section.id, q.id)}
                                                            className="p-2 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                                <button
                                                    onClick={() => handleAddQuestion(section.id)}
                                                    className="w-full py-3 border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-xl text-gray-400 hover:text-blue-500 hover:border-blue-100 dark:hover:border-blue-900/30 transition-all text-sm font-medium"
                                                >
                                                    + Add Another Question
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-6">
                        <Card className="p-6 border-none shadow-sm dark:bg-gray-800">
                            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-6 flex items-center">
                                <GlobeAltIcon className="w-4 h-4 mr-2" />
                                Active Languages
                            </h2>
                            <div className="grid grid-cols-1 gap-2">
                                {SUPPORTED_LANGUAGES.map(lang => {
                                    const isActive = activeLanguages.includes(lang.code);
                                    return (
                                        <button
                                            key={lang.code}
                                            onClick={() => toggleLanguage(lang.code)}
                                            className={`flex items-center justify-between p-3 rounded-xl border transition-all ${isActive
                                                ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                                                : 'bg-white border-gray-100 dark:bg-gray-900 dark:border-gray-800 grayscale'
                                                }`}
                                        >
                                            <div className="flex items-center space-x-3">
                                                <span className="text-xl">{lang.flag}</span>
                                                <span className={`text-sm font-semibold ${isActive ? 'text-blue-700 dark:text-blue-400' : 'text-gray-500'}`}>
                                                    {lang.name}
                                                </span>
                                            </div>
                                            {isActive && <CheckIcon className="w-5 h-5 text-blue-600" />}
                                        </button>
                                    );
                                })}
                            </div>
                        </Card>

                        <Card className="p-6 border-none shadow-sm dark:bg-gray-800">
                            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-6">Settings</h2>
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                        <p className="text-sm font-bold text-gray-900 dark:text-white">Active</p>
                                        <p className="text-xs text-gray-500">Allow users to take survey</p>
                                    </div>
                                    <button
                                        onClick={() => setSurveyData({ ...surveyData, is_active: !surveyData.is_active })}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${surveyData.is_active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${surveyData.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                        <p className="text-sm font-bold text-gray-900 dark:text-white">Anonymous</p>
                                        <p className="text-xs text-gray-500">Don't track user identity</p>
                                    </div>
                                    <button
                                        onClick={() => setSurveyData({ ...surveyData, is_anonymous: !surveyData.is_anonymous })}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${surveyData.is_anonymous ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${surveyData.is_anonymous ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Est. Minutes</label>
                                    <input
                                        type="number"
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                        value={surveyData.estimated_minutes}
                                        onChange={(e) => setSurveyData({ ...surveyData, estimated_minutes: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>
                        </Card>
                    </div>
                </div >

                {/* Question Builder Modal */}
                {
                    isQuestionModalOpen && createPortal(
                        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 outline-none focus:outline-none overflow-y-auto">
                            <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border-none rounded-2xl transform transition-all animate-in fade-in zoom-in duration-300">
                                <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-800">
                                    <h3 className="text-xl font-bold">Add Question</h3>
                                    <button onClick={() => setIsQuestionModalOpen(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"><XMarkIcon className="w-6 h-6" /></button>
                                </div>
                                <div className="p-6 overflow-y-auto bg-gray-50 dark:bg-gray-900 flex-1 space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Question Type</label>
                                            <select
                                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                                                value={currentQuestion.type_id}
                                                onChange={(e) => setCurrentQuestion({ ...currentQuestion, type_id: e.target.value, options: [] })}
                                            >
                                                {questionTypes.map(type => (
                                                    <option key={type.id} value={type.id}>{type.type_name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {activeLanguages.map(lang => (
                                            <div key={lang}>
                                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">
                                                    Question ({lang.toUpperCase()})
                                                </label>
                                                <input
                                                    type="text"
                                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 outline-none"
                                                    value={currentQuestion.title[lang] || ''}
                                                    placeholder={`Enter question in ${lang.toUpperCase()}`}
                                                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, title: { ...currentQuestion.title, [lang]: e.target.value } })}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {hasOptions && (
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Options</label>
                                                <button onClick={handleAddOption} className="text-sm text-blue-500 font-bold hover:text-blue-600 transition-colors">+ Add Option</button>
                                            </div>
                                            <div className="space-y-4">
                                                {currentQuestion.options.map((option, idx) => (
                                                    <div key={option.id} className="p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700 group relative">
                                                        <button
                                                            onClick={() => handleRemoveOption(option.id)}
                                                            className="absolute -top-2 -right-2 p-1.5 bg-red-50 text-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-all hover:bg-red-500 hover:text-white shadow-sm"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            {activeLanguages.map(lang => (
                                                                <input
                                                                    key={lang}
                                                                    type="text"
                                                                    placeholder={`Option (${lang.toUpperCase()})`}
                                                                    className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                                                                    value={option.text[lang] || ''}
                                                                    onChange={(e) => {
                                                                        const newOptions = [...currentQuestion.options];
                                                                        newOptions[idx].text[lang] = e.target.value;
                                                                        setCurrentQuestion({ ...currentQuestion, options: newOptions });
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                                {currentQuestion.options.length === 0 && <p className="text-center text-xs text-gray-500 py-4 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">No options added yet</p>}
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex items-center space-x-2 pt-4 border-t border-gray-100 dark:border-gray-800">
                                        <input
                                            type="checkbox"
                                            id="required"
                                            checked={currentQuestion.is_required}
                                            onChange={(e) => setCurrentQuestion({ ...currentQuestion, is_required: e.target.checked })}
                                            className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                                        />
                                        <label htmlFor="required" className="text-sm font-medium text-gray-700 dark:text-gray-300">Bu soru zorunlu olsun</label>
                                    </div>
                                </div>
                                <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-800 flex justify-end space-x-3">
                                    <button onClick={() => setIsQuestionModalOpen(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">Cancel</button>
                                    <button onClick={handleSaveQuestion} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-semibold">Add to Section</button>
                                </div>
                            </Card>
                        </div>,
                        document.body
                    )
                }
            </div >
        </>
    );
};

export default SurveyCreate;
